import { computed } from 'vue';
import useUpc from '~/modules/upc/composables/useUpc';
import useBopsData from '~/composables/useBopsData';
import { bossPickupMessagging } from '~/modules/add-to-bag/utils/bossPickupMessagging.mcom';
import { getAvailabilityMessage } from '~/modules/add-to-bag/utils/getAvailabilityMessage.bcom';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { toUTagBool } from '~/modules/add-to-bag/utils/bool';
import useSize from '~/modules/size/composables/useSize';
import { useTrueFit } from '~/modules/truefit/composables/useTrueFit';
import { bopsPickupMessagging, getHolidayMessage } from '../../utils/bcom';
import { useATBPriceBCOM } from '../useATBPrice.bcom';

/** @typedef {import('../../types').ATBBopsAvailability} ATBBopsAvailability */

export const useATBUTagBCOM = () => {
  const { selectedUpcId, upcData } = useUpc();
  const { selectedSizeId } = useSize();
  const { nearestStore } = useBopsData();
  const { killswitches } = useKillswitches();
  const { lastSelectedSize } = useTrueFit();
  const atbPricing = useATBPriceBCOM();

  /**
   * @type {import('vue').ComputedRef<import('@nuxt-common/media-card-large/app/composables/useUpc/types').UseUpc.Upc>}
   * TODO Move to useUpc
   */
  const upc = computed(() => upcData.value.upcs[selectedUpcId.value]);

  /**
   * @type {import('vue').ComputedRef<ATBBopsAvailability>}
   */
  const availability = computed(() => {
    if (upc.value) {
      return { bopsAvailable: upc.value.availability.bopsAvailable, bossAvailable: upc.value.availability.bossAvailable };
    }
    return { bopsAvailable: false, bossAvailable: false };
  });

  const pickupEligibility = computed(() => {
    const { deliveryDay, cutOffTime, curbsideCutOffTime, otherDate, details, pickupOptions } = nearestStore.value || {};

    const bopsLinkTagMessage = bopsPickupMessagging({
      inStorePickupEligibility: details?.inStorePickupEligibility,
      curbsidePickupEligibility: details?.curbsidePickupEligibility,
      deliveryDay,
      otherDate,
      pickupOptions,
      curbsideCutOffTime,
      cutOffTime,
      availability: availability.value,
    });

    const bossLinkTagMessage = bossPickupMessagging({
      inStorePickupEligibility: details?.inStorePickupEligibility,
      curbsidePickupEligibility: details?.curbsidePickupEligibility,
      availabilityMessage: bopsLinkTagMessage,
    });

    const { bopsAvailable, bossAvailable } = availability.value;
    return !bopsAvailable && !bossAvailable ? ['NA'] : [bopsLinkTagMessage || bossLinkTagMessage];
  });

  return computed(() => {
    const { marketPlaceEnabled } = killswitches;
    const productAvailabilityMessage = getAvailabilityMessage({ upc: upc.value, marketPlaceEnabled });

    return {
      holiday_eligibility: [getHolidayMessage(upc.value)],
      pickup_eligibility: pickupEligibility.value,
      product_availability_message: productAvailabilityMessage,
      product_low_availability: toUTagBool(upc.value?.availability.lowAvailability),
      product_pricing_state: [String(atbPricing.value?.priceTypeId || 'ZERO')],
      product_promotion_available: toUTagBool(atbPricing.value?.priceType?.onSale),
      is_product_size_predictor_atb: toUTagBool(selectedSizeId.value === lastSelectedSize.value),
    };
  });
};
